import { computed } from 'vue'

const WAREHOUSE_STORAGE_KEY = 'currentWarehouseId'
const LEGACY_WAREHOUSE_STORAGE_KEY = 'shipment.warehouseId'

export const WAREHOUSE_ALL = 'all'
export const WAREHOUSE_NONE = 'none'
const WAREHOUSE_SENTINELS = [WAREHOUSE_ALL, WAREHOUSE_NONE]

const STATUS_KEYS = {
  draft: 'shipment.status.draft',
  planned: 'shipment.status.planned',
  arrived: 'shipment.status.arrived',
  departed: 'shipment.status.departed',
  canceled: 'shipment.status.canceled',
}

const STATUS_COLORS = {
  draft: 'neutral',
  planned: 'warning',
  arrived: 'primary',
  departed: 'success',
  canceled: 'error',
}

const ORIGIN_KEYS = {
  manual: 'shipment.origin.manual',
  schedule: 'shipment.origin.schedule',
  next: 'shipment.origin.next',
  auto_move: 'shipment.origin.auto_move',
}

const BLOCKER_KEYS = {
  empty_shipment: 'shipment.blockers.empty_shipment',
  supply_in_other_shipment: 'shipment.blockers.supply_in_other_shipment',
  supply_not_ready: 'shipment.blockers.supply_not_ready',
  supply_unavailable: 'shipment.blockers.supply_unavailable',
  supply_not_loaded: 'shipment.blockers.supply_not_loaded',
  warehouse_mismatch: 'shipment.blockers.warehouse_mismatch',
  driver_missing: 'shipment.blockers.driver_missing',
  vehicle_missing: 'shipment.blockers.vehicle_missing',
  shipment_not_editable: 'shipment.blockers.shipment_not_editable',
  cutoff_passed: 'shipment.blockers.cutoff_passed',
  shipment_scan_not_available: 'shipment.blockers.shipment_scan_not_available',
}

const SCAN_RESULT_KEYS = {
  loaded: 'shipment.scan.result.loaded',
  not_ready: 'shipment.scan.result.not_ready',
  other_shipment: 'shipment.scan.result.other_shipment',
  not_in_shipment: 'shipment.scan.result.not_in_shipment',
  unknown: 'shipment.scan.result.unknown',
}

const SUPPLY_EVENT_KEYS = {
  added: 'shipment.history.events.added',
  removed: 'shipment.history.events.removed',
  moved_in: 'shipment.history.events.moved_in',
  moved_out: 'shipment.history.events.moved_out',
  loaded: 'shipment.history.events.loaded',
  unloaded: 'shipment.history.events.unloaded',
  auto_moved_after_cancel: 'shipment.history.events.auto_moved_after_cancel',
}

export function useShipmentScope() {
  const { t } = useI18n()
  const warehouses = useState('warehouseScope.warehouses', () => [])
  const selection = useState('warehouseScope.selection', () => null)
  const loading = useState('warehouseScope.loading', () => false)
  const loaded = useState('warehouseScope.loaded', () => false)
  const error = useState('warehouseScope.error', () => null)
  const inFlight = useState('warehouseScope.inFlight', () => null)

  const isAll = computed(() => selection.value === WAREHOUSE_ALL)
  const isNone = computed(() => selection.value === WAREHOUSE_NONE)
  const warehouseId = computed(() =>
    selection.value && !WAREHOUSE_SENTINELS.includes(selection.value)
      ? selection.value
      : null
  )
  const current = computed(
    () => warehouses.value.find((item) => item.id === warehouseId.value) || null
  )
  const timezone = computed(() => current.value?.timezone || 'Europe/Moscow')
  const hasMultiple = computed(() => warehouses.value.length > 1)
  const ready = computed(() => Boolean(warehouseId.value))
  const scopeLabel = computed(() => {
    if (isAll.value) return t('warehouseScope.all')
    if (isNone.value) return t('warehouseScope.none')
    return current.value?.title || t('shipment.scope.noWarehouse')
  })
  const scopeOptions = computed(() => {
    if (!hasMultiple.value) return warehouses.value

    return [
      { id: WAREHOUSE_ALL, title: t('warehouseScope.all') },
      { id: WAREHOUSE_NONE, title: t('warehouseScope.none') },
      ...warehouses.value,
    ]
  })

  async function loadWarehouses(force = false) {
    if (inFlight.value) return inFlight.value
    if (loaded.value && !force) return warehouses.value

    inFlight.value = (async () => {
      loading.value = true
      error.value = null

      try {
        const response = await useApi('api/warehouse?count=100')
        const items = response?.data?.items || []
        warehouses.value = items.map((item) => ({
          id: String(item.id),
          title: item.title || item.address || String(item.id),
          address: item.address || '',
          timezone: item.timezone || 'UTC',
        }))
      } catch (err) {
        error.value = err
        warehouses.value = []
      } finally {
        loading.value = false
        loaded.value = true
      }

      const stored = import.meta.client
        ? localStorage.getItem(WAREHOUSE_STORAGE_KEY) ||
          localStorage.getItem(LEGACY_WAREHOUSE_STORAGE_KEY)
        : null
      const allowScopeOptions = warehouses.value.length > 1
      const storedKnown =
        WAREHOUSE_SENTINELS.includes(stored) ||
        warehouses.value.some((item) => item.id === stored)
      const currentKnown =
        WAREHOUSE_SENTINELS.includes(selection.value) ||
        warehouses.value.some((item) => item.id === selection.value)

      let nextSelection = null

      if (!allowScopeOptions) {
        nextSelection = warehouses.value[0]?.id || null
      } else if (storedKnown) {
        nextSelection = stored
      } else if (currentKnown) {
        nextSelection = selection.value
      } else {
        nextSelection = warehouses.value[0]?.id || null
      }

      selection.value = nextSelection

      if (import.meta.client) {
        if (nextSelection) {
          localStorage.setItem(WAREHOUSE_STORAGE_KEY, nextSelection)
          localStorage.removeItem(LEGACY_WAREHOUSE_STORAGE_KEY)
        } else {
          localStorage.removeItem(WAREHOUSE_STORAGE_KEY)
        }
      }

      return warehouses.value
    })()

    try {
      return await inFlight.value
    } finally {
      inFlight.value = null
    }
  }

  function selectWarehouse(id) {
    let nextSelection = id || null

    if (WAREHOUSE_SENTINELS.includes(nextSelection) && !hasMultiple.value) {
      nextSelection = warehouses.value[0]?.id || null
    }

    selection.value = nextSelection

    if (import.meta.client) {
      if (nextSelection) {
        localStorage.setItem(WAREHOUSE_STORAGE_KEY, nextSelection)
        localStorage.removeItem(LEGACY_WAREHOUSE_STORAGE_KEY)
      } else {
        localStorage.removeItem(WAREHOUSE_STORAGE_KEY)
      }
    }
  }

  return {
    warehouses,
    selection,
    warehouseId,
    current,
    timezone,
    hasMultiple,
    ready,
    isAll,
    isNone,
    scopeLabel,
    scopeOptions,
    loading,
    loaded,
    error,
    loadWarehouses,
    selectWarehouse,
  }
}

function buildQuery(params = {}) {
  const query = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return

    if (Array.isArray(value)) {
      if (!value.length) return
      query.append(key, value.join(','))
      return
    }

    if (typeof value === 'boolean') {
      query.append(key, value ? '1' : '0')
      return
    }

    query.append(key, String(value))
  })

  const result = query.toString()

  return result ? `?${result}` : ''
}

export function useShipmentApi() {
  const { warehouseId } = useShipmentScope()

  async function request(url, options = {}) {
    const response = await useApi(url, options)
    if (response?.success === false) {
      const error = new Error(response?.errors?.title || 'Shipment API error')
      error.data = response
      throw error
    }

    return response?.data !== undefined ? response.data : response
  }

  function scopedBody(body = {}) {
    return { warehouseId: warehouseId.value, ...body }
  }

  const list = (params = {}) =>
    request(`api/shipment${buildQuery({ warehouseId: warehouseId.value, ...params })}`)

  const show = (id, params = {}) =>
    request(
      `api/shipment/${id}${buildQuery({
        warehouseId: warehouseId.value,
        ...params,
      })}`
    )

  const supplies = (id, params = {}) =>
    request(
      `api/shipment/${id}/supplies${buildQuery({
        warehouseId: warehouseId.value,
        ...params,
      })}`
    )

  const history = (id, params = {}) =>
    request(
      `api/shipment/${id}/history${buildQuery({
        warehouseId: warehouseId.value,
        ...params,
      })}`
    )

  const next = (destination = null) =>
    request(
      `api/shipment/next${buildQuery({
        warehouseId: warehouseId.value,
        destination,
      })}`
    )

  const create = (payload) =>
    request('api/shipment', {
      method: 'POST',
      body: scopedBody(payload),
      headers: {
        'Idempotency-Key':
          globalThis.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      },
    })

  const update = (id, payload) =>
    request(`api/shipment/${id}`, {
      method: 'PATCH',
      body: scopedBody(payload),
    })

  const plan = (id, payload = {}) =>
    request(`api/shipment/${id}/plan`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const arrive = (id, payload = {}) =>
    request(`api/shipment/${id}/arrive`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const depart = (id, payload = {}) =>
    request(`api/shipment/${id}/depart`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const cancel = (id, payload) =>
    request(`api/shipment/${id}/cancel`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const validate = (id, payload = {}) =>
    request(`api/shipment/${id}/validate`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const scan = (id, payload) =>
    request(`api/shipment/${id}/scan`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const addSupplies = (id, payload) =>
    request(`api/shipment/${id}/supplies`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const removeSupply = (id, supplyId, payload = {}) =>
    request(`api/shipment/${id}/supplies/${supplyId}`, {
      method: 'DELETE',
      body: scopedBody(payload),
    })

  const moveSupply = (id, supplyId, toShipmentId, reason = null) =>
    request(`api/shipment/${id}/supplies/${supplyId}/move`, {
      method: 'POST',
      body: scopedBody({ toShipmentId, reason }),
    })

  const addReady = (id, payload = {}) =>
    request(`api/shipment/${id}/add-ready`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  const moveUnready = (id, payload = {}) =>
    request(`api/shipment/${id}/move-unready`, {
      method: 'POST',
      body: scopedBody(payload),
    })

  async function document(id, type) {
    const response = await useApi(
      `api/shipment/${id}/${type}${buildQuery({ warehouseId: warehouseId.value })}`
    )

    if (response instanceof Blob) return response

    return new Blob([response], { type: 'application/pdf' })
  }

  const listSchedules = (params = {}) =>
    request(
      `api/shipment-schedule${buildQuery({
        warehouseId: warehouseId.value,
        ...params,
      })}`
    )

  const createSchedule = (payload) =>
    request('api/shipment-schedule', {
      method: 'POST',
      body: scopedBody(payload),
    })

  const updateSchedule = (id, payload) =>
    request(`api/shipment-schedule/${id}`, {
      method: 'PATCH',
      body: scopedBody(payload),
    })

  const deleteSchedule = (id) =>
    request(
      `api/shipment-schedule/${id}${buildQuery({ warehouseId: warehouseId.value })}`,
      { method: 'DELETE' }
    )

  return {
    list,
    show,
    supplies,
    history,
    next,
    create,
    update,
    plan,
    arrive,
    depart,
    cancel,
    validate,
    scan,
    addSupplies,
    removeSupply,
    moveSupply,
    addReady,
    moveUnready,
    document,
    listSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  }
}

export function shipmentError(error) {
  const data = error?.data || error?.response?._data || error?.response?.data || {}
  const errors = data?.errors || {}
  const blockers = Array.isArray(errors.blockingReasons)
    ? errors.blockingReasons
    : Array.isArray(errors)
      ? errors
      : []

  const message =
    errors.title ||
    data?.message ||
    (Array.isArray(errors)
      ? errors.map((item) => item?.title || item?.message).filter(Boolean).join('; ')
      : Object.values(errors).flat().filter(Boolean).join('; ')) ||
    error?.message ||
    String(error)

  return {
    status: error?.status || error?.response?.status || error?.response?.statusCode,
    message,
    blockers,
    otherShipmentId: errors.otherShipmentId || null,
    currentVersion: errors.currentVersion ?? null,
    actions: errors.actions || [],
  }
}

export function useShipmentErrors() {
  const { t } = useI18n()

  function details(error) {
    return shipmentError(error)
  }

  function errorText(error) {
    const result = shipmentError(error)

    if (result.status === 409 && result.currentVersion !== null) {
      return t('shipment.errors.version')
    }

    if (result.status === 409 && result.otherShipmentId) {
      return t('shipment.errors.supplyBusy')
    }

    return result.message
  }

  return { details, errorText }
}

export function useShipmentFormat() {
  const { t } = useI18n()

  function translate(key, fallback = '') {
    if (!key) return fallback
    const value = t(key, undefined, { missingWarn: false })
    return value && value !== key ? value : fallback
  }

  const statusLabel = (status) =>
    translate(STATUS_KEYS[status], status || '—')

  const statusColor = (status) => STATUS_COLORS[status] || 'neutral'

  const originLabel = (origin) =>
    translate(ORIGIN_KEYS[origin], origin || '—')

  const blockerLabel = (code) =>
    translate(BLOCKER_KEYS[code], code || '—')

  const scanResultLabel = (result) =>
    translate(SCAN_RESULT_KEYS[result], result || '—')

  const supplyEventLabel = (event) =>
    translate(SUPPLY_EVENT_KEYS[event], event || '—')

  const supplyStatusLabel = (status) => {
    if (status === 'deliver') return t('shipment.supplies.goodForLoading')
    if (status === 'complete') return t('shipment.supplies.acceptedByWb')
    if (['packaging', 'packaged'].includes(status)) return t('shipment.supplies.noWbSticker')
    if (['canceled', 'deleted'].includes(status)) return t('shipment.supplies.unavailable')
    return translate(`statuses.${status}`, status || '—')
  }

  const supplyStatusColor = (status) => {
    if (['deliver', 'complete'].includes(status)) return 'success'
    if (['packaging', 'packaged'].includes(status)) return 'warning'
    return 'error'
  }

  function parts(value, timezone = 'Europe/Moscow') {
    if (!value) return null

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null

    try {
      const formatter = new Intl.DateTimeFormat('ru-RU', {
        timeZone: timezone || 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })

      return Object.fromEntries(
        formatter
          .formatToParts(date)
          .filter((item) => item.type !== 'literal')
          .map((item) => [item.type, item.value])
      )
    } catch {
      return null
    }
  }

  function formatDateTime(value, timezone = 'Europe/Moscow') {
    const p = parts(value, timezone)
    if (!p) return '—'
    return `${p.day}.${p.month}.${p.year}, ${p.hour}:${p.minute}`
  }

  function formatDate(value, timezone = 'Europe/Moscow') {
    const p = parts(value, timezone)
    if (!p) return '—'
    return `${p.day}.${p.month}.${p.year}`
  }

  function formatTime(value, timezone = 'Europe/Moscow') {
    const p = parts(value, timezone)
    if (!p) return '—'
    return `${p.hour}:${p.minute}`
  }

  function shipmentDisplayName(shipment, timezone = 'Europe/Moscow') {
    const name = String(shipment?.name || '').trim()
    if (name) return name

    const p = parts(shipment?.plannedDepartureAt, timezone)
    const time = p ? `${p.hour}:${p.minute}` : ''
    const destination = String(shipment?.destination || '').trim()

    if (time && destination) {
      return t('shipment.name.withTimeAndDestination', { time, destination })
    }
    if (time) return t('shipment.name.withTime', { time })
    if (destination) return t('shipment.name.withDestination', { destination })
    return t('shipment.name.new')
  }

  function renderNameTemplate(template, time, destination) {
    const source = String(template || '').trim()
    if (!source) return ''

    return source
      .replaceAll('{time}', String(time || ''))
      .replaceAll('{destination}', String(destination || ''))
      .trim()
  }

  function scheduleDisplayName(schedule) {
    const name = String(schedule?.name || '').trim()
    if (name) return name

    const arrival = schedule?.arrivalTime ? String(schedule.arrivalTime).slice(0, 5) : ''
    const departure = schedule?.departureTime ? String(schedule.departureTime).slice(0, 5) : ''

    if (arrival || departure) {
      return t('shipment.planner.unnamedSchedule', {
        arrival: arrival || '—',
        departure: departure || '—',
      })
    }

    return t('shipment.name.untitled')
  }

  function scheduleNamePreview(schedule) {
    const name = String(schedule?.name || '').trim()
    if (!name) return scheduleDisplayName(schedule)

    const preview = renderNameTemplate(
      name,
      schedule?.departureTime ? String(schedule.departureTime).slice(0, 5) : '',
      schedule?.destination || ''
    )

    return preview || t('shipment.name.untitled')
  }

  function toLocalInput(value, timezone = 'Europe/Moscow') {
    const p = parts(value, timezone)
    if (!p) return ''
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`
  }

  function toLocalDateInput(value, timezone = 'Europe/Moscow') {
    const p = parts(value, timezone)
    if (!p) return ''
    return `${p.year}-${p.month}-${p.day}`
  }

  function nowLocalInput(timezone = 'Europe/Moscow') {
    return toLocalInput(new Date().toISOString(), timezone) || ''
  }

  function todayLocalDate(timezone = 'Europe/Moscow') {
    return toLocalDateInput(new Date().toISOString(), timezone) || ''
  }

  return {
    t,
    translate,
    statusLabel,
    statusColor,
    originLabel,
    blockerLabel,
    scanResultLabel,
    supplyEventLabel,
    supplyStatusLabel,
    supplyStatusColor,
    formatDateTime,
    formatDate,
    formatTime,
    shipmentDisplayName,
    renderNameTemplate,
    scheduleDisplayName,
    scheduleNamePreview,
    toLocalInput,
    toLocalDateInput,
    nowLocalInput,
    todayLocalDate,
  }
}

export function useShipmentOptions() {
  const users = useState('shipment.options.users', () => [])
  const clients = useState('shipment.options.clients', () => [])
  const loading = useState('shipment.options.loading', () => false)
  const loaded = useState('shipment.options.loaded', () => false)

  async function load(force = false) {
    if (loading.value) return
    if (loaded.value && !force) return

    loading.value = true

    try {
      const [userResponse, clientResponse] = await Promise.all([
        useApi('api/user?count=100').catch(() => null),
        useApi('api/client?count=100').catch(() => null),
      ])

      users.value = (userResponse?.data?.items || []).map((item) => ({
        id: String(item.id),
        title:
          [item.surname, item.name, item.middlename].filter(Boolean).join(' ') ||
          item.email ||
          String(item.id),
      }))

      clients.value = (clientResponse?.data?.items || []).map((item) => ({
        id: String(item.id),
        title: item.fullName || item.name || String(item.id),
      }))
    } finally {
      loading.value = false
      loaded.value = true
    }
  }

  return { users, clients, loading, loaded, load }
}

const SHIPMENT_PERMISSION_NAMES = {
  view: 'view shipment',
  create: 'create shipment',
  plan: 'plan shipment',
  arrive: 'arrive shipment',
  depart: 'depart shipment',
  cancel: 'cancel shipment',
  scan: 'scan shipment',
  overrideScan: 'override shipment scan',
  overrideCutoff: 'override shipment cutoff',
  moveSupply: 'move shipment supply',
  print: 'print shipment',
  schedule: 'manage shipment schedule',
}

const emptyShipmentPermissions = () => ({
  view: false,
  create: false,
  plan: false,
  arrive: false,
  depart: false,
  cancel: false,
  scan: false,
  overrideScan: false,
  overrideCutoff: false,
  moveSupply: false,
  print: false,
  schedule: false,
})

let shipmentPermissionsPromise = null

export function useShipmentPermissions() {
  const state = useState('shipment.permissions', emptyShipmentPermissions)
  const loaded = useState('shipment.permissions.loaded', () => false)
  const loading = useState('shipment.permissions.loading', () => false)

  // view есть в JWT (dotted), остальные действия — сырые имена из каталога.
  if (!state.value.view && useCan('shipment.view')) {
    state.value.view = true
  }

  if (!loaded.value && !shipmentPermissionsPromise) {
    loading.value = true

    shipmentPermissionsPromise = useApi('api/catalog/shipment')
      .then((response) => {
        const names = new Set(response?.data?.permissions || [])

        Object.entries(SHIPMENT_PERMISSION_NAMES).forEach(([key, name]) => {
          state.value[key] = names.has(name)
        })
      })
      .catch(() => {
        // Оставляем view-фолбэк из JWT; остальные действия будут недоступны.
      })
      .finally(() => {
        loaded.value = true
        loading.value = false
        shipmentPermissionsPromise = null
      })
  }

  return state.value
}
